import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useSearchParams } from "react-router-dom";
import { deleteInvoiceGroup, getInvoiceGroups } from "src/api/invoice";
import CustomConfirmDialog from "src/components/CustomConfirmDialog";
import CustomDataTable from "src/components/CustomDataTable";
import CustomDropdown, { dropdownActionVariants } from "src/components/CustomDropdown";
import CustomSheet from "src/components/CustomSheet";
import CreateInvoiceGroup from "src/components/modals/CreateInvoiceGroup";
import InvoiceGroupMembers from "src/components/modals/InvoiceGroupMembers";
import { Button } from "src/components/ui/button";
import SearchInput from "src/components/widgets/SearchInput";
import { cn, getCustomizationValue, showSuccessToast } from "src/lib/utils";
import { useAuth } from "src/providers/AuthProvider";
import { EllipsisIcon } from "src/svg";

const InvoiceGroups = () => {
	const { user } = useAuth();
	const queryClient = useQueryClient();
	const [searchParams] = useSearchParams();
	const search = searchParams.get("search");
	const page = searchParams.get("page") || 1;
	const limit = searchParams.get("limit") || 10;

	const { data, isLoading } = useQuery({
		queryKey: ["invoice-groups", page, limit, search],
		queryFn: () => getInvoiceGroups({ page, limit, search }),
	});

	const { mutate: deleteInvoiceGroupMutate } = useMutation(deleteInvoiceGroup, {
		onSuccess: () => {
			showSuccessToast(cn(getCustomizationValue(user, "invoice_label", true), " group deleted successfully"));
			queryClient.invalidateQueries(["invoice-groups"]);
		},
	});

	const columns = [
		{ name: "S/N", selector: (_, index) => index + 1, width: "100px" },
		{ name: "Name", selector: (row) => row.name, grow: 1.25 },
		{
			name: "Action",
			cell: (row) => (
				<CustomDropdown
					trigger={
						<Button variant="hover">
							<EllipsisIcon />
						</Button>
					}
					actions={[
						{
							label: (
								<CustomSheet
									title={cn("Update ", getCustomizationValue(user, "invoice_group_label", true))}
									trigger={<span>Update {getCustomizationValue(user, "invoice_group_label", true)}</span>}
								>
									<CreateInvoiceGroup invoiceGroup={row} />
								</CustomSheet>
							),
							asChild: true,
						},
						{
							label: (
								<CustomSheet title={cn("Update Members")} trigger={<span>Update Members</span>}>
									<InvoiceGroupMembers uuid={row.uuid} />
								</CustomSheet>
							),
							asChild: true,
						},
						{
							label: (
								<CustomConfirmDialog
									trigger={
										<span className={dropdownActionVariants({ variant: "destructive" })}>
											Delete {getCustomizationValue(user, "invoice_group_label", true)}
										</span>
									}
									mutateFn={() => deleteInvoiceGroupMutate({ uuid: row.uuid })}
								/>
							),
							asChild: true,
						},
					]}
				/>
			),
			grow: 0.5,
		},
	];

	const groups = data?.data?.data;
	const totalRow = data?.data?.total;

	return (
		<div className="settings_cont_ins tw-grid">
			<div className="pageTop tw-flex tw-items-center tw-justify-between">
				<h2>{getCustomizationValue(user, "invoice_group_label")}</h2>
				<div className="tw-flex tw-items-center tw-gap-4">
					<SearchInput name="search" placeholder="Search..." />
					<CustomSheet
						title={cn("Create ", getCustomizationValue(user, "invoice_group_label", true))}
						trigger={<button className="green-btn">Create {getCustomizationValue(user, "invoice_group_label", true)}</button>}
					>
						<CreateInvoiceGroup />
					</CustomSheet>
				</div>
			</div>
			<CustomDataTable columns={columns} data={groups} progressPending={isLoading} paginationTotalRows={totalRow} />
		</div>
	);
};

export default InvoiceGroups;
